import React, { useEffect, useState } from "react";
import { Receipt } from "lucide-react";
import TicketsListSkeleton from "../Skeletons/TicketsListSkeleton";
import MonthPicker from "./MonthPicker";
import PaymentList from "./PaymentList";
import DashboardCard from "../components/DashboardCard";
import DashboardEmptyState from "../components/DashboardEmptyState";
import KenloPropertyFilter from "../components/KenloPropertyFilter";
import { IPayment, IPaginationMeta, IVisitPeriod } from "../types/ownerDashboard.types";
import {
  formatPaymentsPeriodLabel,
  paymentsPeriodToMonthValue,
} from "../utils/formatPaymentsPeriodLabel";
import { KenloPropertyOption } from "../utils/getKenloPropertyOptions";
import { RentedPropertyCatalog } from "../utils/buildRentedPropertyCatalog";

export default function Payments({
  paymentsList,
  paymentsPeriod,
  paymentsPagination = null,
  isLoading = false,
  isError = false,
  onRetry,
  smDown,
  mdDown = false,
  lgDown = false,
  propertyOptions = [],
  selectedKenloPropertyId = null,
  onPropertyChange,
  onPaymentsPeriodChange,
  onPaymentsPageChange,
  rentedPropertyCatalog,
}: {
  paymentsList: IPayment[];
  paymentsPeriod: IVisitPeriod;
  paymentsPagination?: IPaginationMeta | null;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  smDown: boolean;
  mdDown?: boolean;
  lgDown?: boolean;
  propertyOptions?: KenloPropertyOption[];
  selectedKenloPropertyId?: string | null;
  onPropertyChange?: (kenloPropertyId: string | null) => Promise<void>;
  onPaymentsPeriodChange?: (monthValue: string) => Promise<void>;
  onPaymentsPageChange?: (page: number) => Promise<void>;
  rentedPropertyCatalog?: RentedPropertyCatalog;
}) {
  const [ticketsList, setTicketsList] = useState<IPayment[]>(paymentsList);
  const [updatingTicketsList, setUpdatingTicketsList] = useState(false);
  const isCompact = smDown || mdDown;

  useEffect(() => {
    setTicketsList(paymentsList);
  }, [paymentsList]);

  async function handleSelectDate(date: string) {
    if (!onPaymentsPeriodChange) {
      return;
    }

    setUpdatingTicketsList(true);
    try {
      await onPaymentsPeriodChange(date);
    } catch (response) {
      console.log(response);
    }
    setUpdatingTicketsList(false);
  }

  async function handlePropertyChange(kenloPropertyId: string | null) {
    if (!onPropertyChange) {
      return;
    }

    setUpdatingTicketsList(true);
    try {
      await onPropertyChange(kenloPropertyId);
    } catch (response) {
      console.log(response);
    }
    setUpdatingTicketsList(false);
  }

  const padding = isCompact ? "1rem" : lgDown ? "1.25rem" : "1.5rem";
  const paymentsMonthLabel = formatPaymentsPeriodLabel(paymentsPeriod);
  const selectedMonthValue = paymentsPeriodToMonthValue(paymentsPeriod);
  const showInitialSkeleton = isLoading && ticketsList.length === 0;
  const showEmptyPayments = !isLoading && !isError && ticketsList.length === 0;

  if (showInitialSkeleton) {
    return (
      <DashboardCard padding={padding}>
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="h-6 w-48 animate-pulse rounded-full bg-white/[0.08]" />
            <div className="h-9 w-36 animate-pulse rounded-full bg-white/[0.06]" />
          </div>
          <div className="h-px w-full bg-white/[0.08]" />
          <TicketsListSkeleton />
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard padding={padding}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Receipt className="h-5 w-5 shrink-0 text-[#6ea8ff]" />
              <h2 className="text-base font-bold text-[#f0f6fc] sm:text-lg">
                Histórico de Pagamentos
              </h2>
            </div>
            <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
              Repasses em {paymentsMonthLabel}
            </p>
          </div>
          <div className="grid w-full grid-cols-1 gap-3 sm:w-auto sm:grid-cols-2 sm:items-end">
            <MonthPicker value={selectedMonthValue} onDateSelect={handleSelectDate} />
            <KenloPropertyFilter
              properties={propertyOptions}
              selectedKenloPropertyId={selectedKenloPropertyId}
              onSelectProperty={(value) => {
                void handlePropertyChange(value);
              }}
            />
          </div>
        </div>

        <div className="h-px w-full bg-white/[0.08]" />

        {isError && ticketsList.length === 0 ? (
          <DashboardEmptyState
            align="center"
            title="Repasses indisponíveis"
            description="Não foi possível carregar os repasses. Tente novamente em instantes."
            action={
              onRetry ? (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117] transition-opacity hover:opacity-90"
                >
                  Tentar novamente
                </button>
              ) : undefined
            }
            className="py-4"
          />
        ) : updatingTicketsList || (isLoading && ticketsList.length > 0) ? (
          <TicketsListSkeleton />
        ) : showEmptyPayments ? (
          <PaymentList bills={[]} />
        ) : (
          <PaymentList
            bills={ticketsList}
            pagination={paymentsPagination}
            rentedPropertyCatalog={rentedPropertyCatalog}
            onPageChange={
              onPaymentsPageChange
                ? (page) => {
                    void onPaymentsPageChange(page);
                  }
                : undefined
            }
          />
        )}
      </div>
    </DashboardCard>
  );
}
